import React, {useContext, useMemo, useState} from 'react';
import {observer} from 'mobx-react';
import {Dropdown} from '@carbon/react';
import {NetworkContext} from '../../lib/context';
import {formatRosterLabel, rosterKey} from '../../lib/roster-label';
import type {RosterRef} from '../../lib/stores/roster-comparison-store';
import './primary-roster-select.scss';

export type Props = {
    // Roster picked in My Files (the route's roster); any of its revisions can be the primary
    rosterId?: string;
    // Called once the primary role has moved to the chosen revision, e.g. to show it in the URL
    onPrimaryChange?: (roster: RosterRef) => void;
};

type PrimaryRosterOption = {
    id: string;
    rosterId: string;
    revisionId: string;
    label: string;
};

const optionToString = (option: PrimaryRosterOption | null): string => option?.label ?? '';

/**
 * Primary Roster select - chooses which roster the comparison is anchored on
 *
 * Offers the revisions of the roster picked in My Files (Original first), leaving out only the
 * current primary. The page makes the route's revision the primary when it opens. The chosen
 * revision replaces the primary, which leaves the selection: a revision already selected as a
 * comparison roster (e.g. added with Search Rosters) becomes the primary where it is, and any
 * other takes the old primary's slot.
 */
const PrimaryRosterSelect = function ({rosterId, onPrimaryChange}: Props) {
    const networkOptimizationStore = useContext(NetworkContext);
    const comparisonStore = networkOptimizationStore.rosterComparisonStore;
    const {primaryRoster} = comparisonStore;
    const {providerRosters} = networkOptimizationStore;

    // Live region text describing the last primary change
    const [announcement, setAnnouncement] = useState('');

    const options = useMemo<PrimaryRosterOption[]>(() => {
        // Selected comparison rosters stay listed: setPrimary promotes them in place, so
        // nothing is added twice, and hiding them would leave no way to make them primary
        const primaryKey = primaryRoster
            ? rosterKey(primaryRoster.rosterId, primaryRoster.revisionId)
            : null;

        return providerRosters
            .filter(
                (r) =>
                    r.rosterId === rosterId && rosterKey(r.rosterId, r.revisionId) !== primaryKey,
            )
            .sort((a, b) => Number(a.revisionId) - Number(b.revisionId))
            .map((r) => ({
                id: rosterKey(r.rosterId, r.revisionId),
                rosterId: r.rosterId,
                revisionId: r.revisionId,
                label: formatRosterLabel(r),
            }));
    }, [providerRosters, primaryRoster, rosterId]);

    // The primary is left out of the list, so the closed field is built from the store
    const selectedOption = useMemo<PrimaryRosterOption | null>(() => {
        if (!primaryRoster) {
            return null;
        }
        const {rosterId: primaryRosterId, revisionId} = primaryRoster;
        const id = rosterKey(primaryRosterId, revisionId);
        const details = providerRosters.find((r) => rosterKey(r.rosterId, r.revisionId) === id);
        return {
            id,
            rosterId: primaryRosterId,
            revisionId,
            // A roster missing from the list is named by its ID, as its chip is
            label: formatRosterLabel({
                revisionId,
                rosterName: details?.rosterName || `Roster ${primaryRosterId}`,
                revisionName: details?.revisionName || '',
            }),
        };
    }, [primaryRoster, providerRosters]);

    // Tier-1 blocking states: no roster list yet, or it is empty or failed
    if (providerRosters.length === 0) {
        return null;
    }

    const handleChange = ({selectedItem}: {selectedItem: PrimaryRosterOption | null}) => {
        if (!selectedItem) {
            return;
        }

        const {label} = selectedItem;
        const wasSelected =
            comparisonStore.getRosterSlot(selectedItem.rosterId, selectedItem.revisionId) !== null;
        if (!comparisonStore.setPrimary(selectedItem.rosterId, selectedItem.revisionId)) {
            // The store shows a toast; say it here too, since the field stays on the current primary.
            // Replacing a primary never adds a roster, so the limit is only hit with no primary yet
            setAnnouncement(
                comparisonStore.canAddRoster
                    ? `${label} couldn't be set as the primary roster.`
                    : `Maximum of ${comparisonStore.maxTotalRosters} rosters reached. ` +
                          `Remove one before making ${label} the primary roster.`,
            );
            return;
        }

        const slot = comparisonStore.getRosterSlot(selectedItem.rosterId, selectedItem.revisionId);
        // selectedOption is still the previous primary, which has now left the selection
        if (selectedOption) {
            setAnnouncement(`${label} replaced ${selectedOption.label} as the primary roster (roster ${slot}).`);
        } else {
            setAnnouncement(
                wasSelected
                    ? `${label} (roster ${slot}) set as the primary roster.`
                    : `${label} added as roster ${slot} and set as the primary roster.`,
            );
        }

        onPrimaryChange?.({rosterId: selectedItem.rosterId, revisionId: selectedItem.revisionId});
    };

    return (
        <div className='primary-roster-select' data-testid='primary-roster-select'>
            <Dropdown<PrimaryRosterOption>
                id='primary-roster-select'
                titleText='Primary Roster'
                // Visually hidden; it still names the field for screen readers
                hideLabel
                label='Choose Primary Roster'
                size='sm'
                items={options}
                // Carbon's types omit null, but a controlled null shows the placeholder;
                // undefined would leave the dropdown uncontrolled until a primary is set
                selectedItem={selectedOption as PrimaryRosterOption}
                itemToString={optionToString}
                onChange={handleChange}
                // e.g. the roster's only revision is already the primary
                disabled={options.length === 0}
            />
            <p className='sr-only' role='status' aria-live='polite' aria-atomic='true'>
                {announcement}
            </p>
        </div>
    );
};

export default observer(PrimaryRosterSelect);
